import {useState, useEffect} from 'react';
import {Redirect} from 'react-router-dom';
// @ts-expect-error
import type {ContextRouter} from 'react-router-dom';

import {safelyGetFilterByKey} from 'nutshell-core/filter';
import {Nut_Settings_User} from 'nutshell-core/settings-constants';
import type {FilterObject} from 'nutshell-core/types';

import {writeLocalStorage} from '../../util/localStorage';

import {useGetSessionUser} from '../core/use-get-session-user';
import {useGetSetting} from '../core/use-get-setting';
import {PageContainer} from '../layout/page-container';
import {useOpenableModal} from '../openable-modals';
import {
    CHATBOT_LEARN_MORE_MODAL,
    CHATBOT_THREADS_LEARN_MORE_MODAL,
    SHARED_EMAIL_INBOX_LEARN_MORE_MODAL,
} from '../openable-modals/openable-modals';
import type {ReportURLParams} from '../reports/report-types';

import {AllChannelsPage} from './all-channels-page';
import {ChatPage} from './chat/chat-page';
import {ChatbotThreadsPage} from './chat/chatbot-threads-page';
import {useGetContextDocumentUsage} from './chat/graphql/hooks/use-get-context-document-usage';
import {Dashboard} from './dashboard/dashboard';
import {MessengerPage, InstagramPage} from './facebook';
import {getPageTitleBar, getPageTitle, shouldHidePageTitleBar} from './helpers';
import {SettingsPage} from './settings-page';
import {SetupChatbotBanner} from './setup-chatbot-banner';
import {SharedEmailInboxPage} from './shared-email-inbox/shared-email-inbox-page';
import {SMSPage} from './sms/sms-page';
import {WhatsAppPage} from './whatsapp/whatsapp-page';

export const INBOX_SEARCH = 'INBOX_SEARCH';

type Props = ContextRouter & {
    activeFilters: FilterObject[];
    urlParams: ReportURLParams<'contacts'>;
    onParamChange: (arg1: any, arg2: boolean) => any;
    onRemoveAllFilters: () => void;
};

export function InboxPage(props: Props) {
    const {user} = useGetSessionUser();
    const userCanAccessSettings = user && user.permissions.canAccessSetup;
    const {activeFilters} = props;
    const [assigneeIds, setAssigneeIds] = useState([]);

    const {contextDocumentUsage, isLoading: isLoadingContextDocumentUsage} =
        useGetContextDocumentUsage();
    const {setting: skippedChatOnboardingSetting} = useGetSetting({
        settingName: Nut_Settings_User.SETTING_HAS_SKIPPED_CHAT_ONBOARDING,
        fetchPolicy: 'cache-first',
    });

    const hasSkippedChatOnboarding =
        skippedChatOnboardingSetting && Boolean(skippedChatOnboardingSetting.value);
    const hasContextDocuments =
        isLoadingContextDocumentUsage ||
        (contextDocumentUsage && contextDocumentUsage.webPagesUsage > 0);
    const shouldShowChatOnboarding = !hasSkippedChatOnboarding && !hasContextDocuments;

    const {onOpen: onOpenChatbotLearnMoreModal} = useOpenableModal(CHATBOT_LEARN_MORE_MODAL);

    const {onOpen: onOpenChatbotThreadsLearnMoreModal} = useOpenableModal(
        CHATBOT_THREADS_LEARN_MORE_MODAL
    );

    const {onOpen: onOpenSharedEmailInboxLearnMoreModal} = useOpenableModal(
        SHARED_EMAIL_INBOX_LEARN_MORE_MODAL
    );

    // For routes like /inbox/leads and /inbox/chatbot-threads that don't use :section param,
    // we need to parse the pathname directly
    const pathParts = props.location.pathname.split('/').filter(Boolean);
    const selectedSection = props.match.params.section || pathParts[1];
    const selectedPage = props.match.params.page;
    const selectedTab = props.match.params.id;

    useEffect(() => {
        if (activeFilters && activeFilters.length > 0) {
            const assigneeFilter = safelyGetFilterByKey(activeFilters, 'owner');

            const ids =
                assigneeFilter && assigneeFilter.data
                    ? // @ts-expect-error
                      assigneeFilter.data.map((filter) => filter.data)
                    : [];

            setAssigneeIds(ids || []);
        } else {
            setAssigneeIds([]);
        }
    }, [activeFilters]);

    useEffect(() => {
        writeLocalStorage(INBOX_SEARCH, props.location.search);
    }, [props.location.search]);

    let pageContent = null;
    let hasWhiteBackground = true;
    switch (selectedSection) {
        case 'dashboard':
            hasWhiteBackground = false;
            pageContent = (
                <Dashboard
                    activeFilters={props.activeFilters}
                    urlParams={props.urlParams}
                    onParamChange={props.onParamChange}
                />
            );
            break;
        case 'settings':
            if (user && !userCanAccessSettings) {
                return <Redirect to='/inbox/sms/open' />;
            }

            pageContent = <SettingsPage {...props} />;
            break;
        case 'chat':
            pageContent = <ChatPage {...props} assigneeIds={assigneeIds} />;
            break;
        case 'messenger':
            pageContent = <MessengerPage {...props} assigneeIds={assigneeIds} />;
            break;
        case 'all':
            pageContent = <AllChannelsPage {...props} assigneeIds={assigneeIds} />;
            break;
        case 'instagram':
            pageContent = <InstagramPage {...props} assigneeIds={assigneeIds} />;
            break;
        case 'whatsapp':
            pageContent = <WhatsAppPage {...props} assigneeIds={assigneeIds} />;
            break;
        case 'email':
            if (!NutClientConfig.hasSharedEmailInboxFF) {
                return <Redirect to='/inbox/dashboard' />;
            }
            pageContent = <SharedEmailInboxPage {...props} assigneeIds={assigneeIds} />;
            break;
        case 'chatbot-threads':
            if (shouldShowChatOnboarding) {
                return <Redirect to='/inbox/settings/chat-onboarding' />;
            }
            pageContent = <ChatbotThreadsPage {...props} />;
            break;
        default:
            pageContent = <SMSPage {...props} assigneeIds={assigneeIds} />;
    }

    let setupChatbotBanner = null;
    if (
        selectedSection === 'settings' &&
        selectedPage === 'chat' &&
        !hasContextDocuments &&
        !isLoadingContextDocumentUsage
    ) {
        setupChatbotBanner = <SetupChatbotBanner />;
    }

    return (
        <PageContainer
            title={getPageTitle(selectedSection)}
            pageTitleBar={getPageTitleBar(
                selectedSection,
                selectedPage,
                selectedTab,
                true,
                onOpenChatbotLearnMoreModal,
                onOpenChatbotThreadsLearnMoreModal,
                onOpenSharedEmailInboxLearnMoreModal
            )}
            whiteBackground={hasWhiteBackground}
            noPadding={true}
            darkBackground={shouldHidePageTitleBar(selectedSection, selectedPage)}
            flashBanner={setupChatbotBanner}
        >
            <div className='pos-relative flex full-height'>{pageContent}</div>
        </PageContainer>
    );
}
